<script lang="ts">
  import { untrack } from 'svelte';
  import { CHAT } from '../../game/constants/hud';

  interface Props {
    /** Sends the line. The server re-checks length, rate and wording, so this is not the gate. */
    onsend: (text: string) => void;
    /** `hud` closes itself after a send and opens on Enter; `lobby` stays put and always shows. */
    mode?: 'hud' | 'lobby';
  }

  const { onsend, mode = 'lobby' }: Props = $props();

  let field = $state<HTMLInputElement | null>(null);
  let draft = $state('');
  let open = $state(untrack(() => mode === 'lobby'));

  function send(): void {
    const text = draft.trim();
    draft = '';
    if (text !== '') onsend(text);
    if (mode === 'hud') {
      open = false;
      field?.blur();
    }
  }

  function onkeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      send();
      return;
    }
    if (event.key === 'Escape' && mode === 'hud') {
      event.preventDefault();
      draft = '';
      open = false;
      field?.blur();
    }
  }

  function isTyping(target: EventTarget | null): boolean {
    const element = target as { tagName?: string; isContentEditable?: boolean } | null;
    if (!element) return false;
    if (element.isContentEditable === true) return true;
    return element.tagName === 'INPUT' || element.tagName === 'TEXTAREA';
  }

  /** Enter is the one key the game loop does not spend, so it can open the line without stealing a control. */
  $effect(() => {
    if (mode !== 'hud') return;
    const onwindowkey = (event: KeyboardEvent): void => {
      if (event.key !== 'Enter' || open || isTyping(event.target)) return;
      event.preventDefault();
      open = true;
      queueMicrotask(() => field?.focus());
    };
    window.addEventListener('keydown', onwindowkey);
    return () => {
      window.removeEventListener('keydown', onwindowkey);
    };
  });
</script>

{#if open}
  <div class="composer" class:composer--hud={mode === 'hud'} data-testid="chat-composer">
    <input
      bind:this={field}
      bind:value={draft}
      class="composer__field"
      type="text"
      data-testid="chat-input"
      aria-label="Chat message"
      maxlength={CHAT.maxLength}
      placeholder={CHAT.placeholder}
      {onkeydown}
    />
    <button
      class="wis-btn-inline composer__send"
      type="button"
      data-testid="chat-send"
      onclick={send}>Send</button
    >
  </div>
{:else}
  <p class="composer__hint" data-testid="chat-hint">Press Enter to chat</p>
{/if}

<style>
  .composer {
    display: flex;
    gap: 6px;
    pointer-events: auto;
  }

  /* Clear of the minimap, which owns the bottom-left corner out to 130 UI pixels. */
  .composer--hud {
    position: absolute;
    left: 150px;
    bottom: 12px;
    width: 420px;
  }

  .composer__hint {
    position: absolute;
    left: 150px;
    bottom: 14px;
    margin: 0;
    font-family: var(--wis-font);
    font-size: 11px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #7d8699;
    text-shadow: 0 1px 2px #000;
  }

  .composer__field {
    flex: 1 1 auto;
    min-width: 0;
    padding: 6px 8px;
    font-family: var(--wis-font);
    font-size: 12px;
    color: #e6ebf7;
    background: linear-gradient(#12141a, #0b0d11);
    border: 1px solid #3a4152;
    outline: none;
  }

  .composer__field:focus {
    border-color: #d8c07a;
  }

  .composer__send {
    padding: 6px 12px;
    font-size: 11px;
  }
</style>
